import { Event } from "../../../../../Global/Base/Events";
import { IDisposable } from "../../../../../Global/Base/IDisposable";
import { EMPTY_ITEM_RIGHTS_COLLECTION } from "../../../../../Global/Base/Rights/RightTypes";
import { ActionContext, ActionType, IAction } from "../../../../UI.Controls/Actions/IAction";
import { INodeAction } from "../../../../UI.Controls/Actions/INodeAction";
import { MenuType } from "../../../../UI.Controls/Actions/MenuType";
import { ControlParams } from "../../Controls/ControlInterfaces";
import { ContainerToolbarControl } from "../../Toolbar/ToolbarControl";
import { XGAction } from "./XGAction";

export enum NodeActionType { naNode, naPage, naGroup }

/**
 * NodeAction je ovládací prvek realizující uzlový prvek pro organizaci Akcí, Formulářů a Tisků v ToolBarech formulářů.
 * http://sharepoint/vyroba/Dokumentace/NodeAction%20-%20nogen.aspx
 */
export class NodeAction extends XGAction implements ContainerToolbarControl, IDisposable, INodeAction {
    //#region Events
    public itemAdded: Event<INodeAction, IAction> = new Event<INodeAction, IAction>();
    public itemRemoved: Event<INodeAction, IAction> = new Event<INodeAction, IAction>();
    //#endregion Events

    //#region Fields
    public actions: IAction[] = []; //pole akci, ze kterych se vybiraji akce, ktere se budou vkladat do menu
    public ctrlID: string;
    public nodeActionType: NodeActionType;

    private _hasVisibleChild: boolean = false;

    /**
     * Nadrizeny prvek v toolbaru
     */
    public parentAction: ContainerToolbarControl;
    //#endregion Fields

    //#region Properties
    public override get interactive(): boolean {
        return this._interactive;
    }

    /**
    * Slouzi k nastavovani ReadOnly na needitacnich komponentach
    * Zneaktivni tlacitko, kdyz otevru modalni okno.
    */
    public override set interactive(value: boolean) {
        this._interactive = value;
        if (this.nodeActionType != NodeActionType.naNode)
            return;

        this._updateActiveState();
    }

    public override get enabled(): boolean {
        return true;
    }

    /**
     * NodeAction je vzdy povolena, nema nastaveni prav na Enabled
     */
    public override set enabled(_value: boolean) {
        // Prázdná metoda
    }

    public override set visible(value: boolean) {
        if (!value || (this.actions != null && this.actions.length > 0 && this._hasVisibleChild)) // pokud chceme zviditelnit NodeAction, která nemá žádné akce, zůstane neviditelná
            super.visible = value;
    }

    public override get visible(): boolean {
        return super.visible;
    }

    public override get context(): ActionContext {
        return ActionContext.none; // TODO: bude moci být asi v obou menu a bude se řídit podle toho, kam se vytvoří
    }

    public override get actionType(): ActionType {
        return ActionType.node;
    }

    //#endregion Properties

    //#region Constructors
    public static createDefault(): NodeAction {
        return new NodeAction({
            element: document.createElement("div"),
            originalRightsDefinition: EMPTY_ITEM_RIGHTS_COLLECTION,
        });
    }

    constructor(params: ControlParams) {
        super(params);

        const nat = this.getDataAttr("NodeActionType");

        switch (nat) {
            case "naGroup":
                this.nodeActionType = NodeActionType.naGroup;
                break;

            case "naPage":
                this.nodeActionType = NodeActionType.naPage;
                break;

            case "naNode":
            case null:
                this.nodeActionType = NodeActionType.naNode;
                this.setToMenu(MenuType.mtNone);
                break;
        }

        this.element.classList.add("NodeAction");
        this.ctrlID = this.element.getAttribute("data-ctrlid");
        //Node action je pri vytvoreni skryta, protoze akce se do ni pridavaji az pozdeji.
        this.visible = false;
        this._enabled = true;
        this.parentAction = null;
    }
    //#endregion Constructors

    //#region Methods
    protected override async _internalExecute(): Promise<void> {
        // Prázdná metoda
    }

    /**
     * Pokud jsou vsechny podrizene akce neviditelne nebo zadnou akci nema, tak nastavim groupu jako skrytou
     */
    private _checkChildrenAndSetVisibility(): void {
        let newVisible = false;
        for (let i = 0; i < this.actions?.length; i++) {
            if (this.actions[i].visible) {
                newVisible = true;
                break;
            }
        }
        this._hasVisibleChild = newVisible;
        this.visible = newVisible;
    }

    /**Najde masternode*/
    public findMasterNode(ctrlID: string): NodeAction {
        const action: NodeAction = this._findAction(ctrlID);

        if (action != null)
            return action;
        else
            return null;
    }

    /**Najde konkretni akci*/
    private _findAction(ctrlID: string): NodeAction {
        for (const a of this.actions) {
            if (a instanceof NodeAction) {
                const action: NodeAction = a;
                if (ctrlID != null && action.ctrlID == ctrlID) {
                    return action;
                } else
                    action.findMasterNode(ctrlID);
            }
        }

        return null;
    }

    private _pushActionToIndex(action: IAction, index: number): void {
        if (index < this.actions.length) {
            this.actions.splice(index, 0, action);
        } else {
            this.actions.push(action);
        }
    }

    public addAction(action: IAction): void {
        let position = this.actions.length;

        if (action.priority) {
            for (let i = 0; i < this.actions.length; i++) {
                if (this.actions[i].priority > action.priority) {
                    position = i;
                    break;
                }
            }
        }
        this._pushActionToIndex(action, position);
        if (action instanceof INodeAction)
            action.parentAction = this;
        this._events.add(action.visibilityChanged, () => this._checkChildrenAndSetVisibility());
        this._checkChildrenAndSetVisibility();
        this.itemAdded.trigger(this, action);
    }

    /**
     * Odstrani akci z nadrizeneho elementu
     */
    public removeAction(action: IAction): void {
        this.actions?.splice(this.actions.indexOf(action), 1);
        //if (action.ToolbarButton.Element)
        //    action.ToolbarButton.Element.remove();
        if (action instanceof INodeAction)
            action.parentAction = null;
        this._checkChildrenAndSetVisibility();
        this.itemRemoved.trigger(this, action);
    }

    //TODO: MV - toto se asi stejne nikde nevola
    //public CreateButton(): XGToolbarMenuButton {
    //    return new ToolbarMenuButton(this.Element, this);
    //}
    //#endregion Methods

    //#region dispose
    public override dispose(): void {
        this.parentAction?.removeAction(this);
        this.actions?.forEach(action => action.dispose());
        this.actions = [];
        this.itemAdded.removeAll();
        this.itemRemoved.removeAll();
        super.dispose();
    }

    //#endregion dispose
}
